/**
 * Section 12e — Synthetic Symbols: chip-built option baskets persisted as
 * tradable pseudo-instruments on the Charts page. Kept in sync with
 * services/options/syntheticSymbol.service.ts + syntheticTrigger.service.ts
 * and the ChartsPage SYNTH mode (docs/SYNTHETIC-SYMBOLS-PLAN.md).
 */
import React from 'react';
import { Typography, List, ListItem, ListItemText, Chip, Box } from '@mui/material';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const LI = ({ children }: { children: React.ReactNode }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3, py: 0.3 }}><ListItemText primary={children} /></ListItem>
);
const Sw = ({ c }: { c: string }) => (
  <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', bgcolor: c, mr: 0.5, verticalAlign: 'middle' }} />
);

const Section_synthetic_symbols: GuideSection = {
  id: 'synthetic-symbols',
  label: 'Synthetic Symbols',
  group: 'pages',
  icon: <AutoAwesomeMotionIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Synthetic Symbols — trade an option basket like one instrument</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A synthetic symbol is a saved multi-leg option basket that behaves like a normal tradable symbol on the{' '}
        <OpenInApp to="/charts" label="Charts" /> page: it has a name in the symbol pane, a chart of its own combined
        premium, and BUY/SHORT buttons — while the app transparently places and manages the real multi-leg option
        orders underneath.
      </Typography>

      <H id="panes">The three panes (below the symbol list)</H>
      <List dense>
        <LI><strong>Synthetic · saved</strong> — synthetics you saved manually from the chart's Build dialog
          (<em>💾 Save as symbol</em>) that aren't live yet. Any chip type — directional or neutral (Straddle, Iron
          Condor, …).</LI>
        <LI><strong>Synthetic · live</strong> — synthetics whose trigger has fired: the multi-leg position is open and
          managed by the basket monitor. They drop out when the basket closes. <strong>Running auto-strategy cycles
          (Options AT: ThetaMiner, Rudra, Sudarshan, Pinak) also appear here</strong> as read-only rows named{' '}
          <code>{'{TM1|TM2|RUD|SUD|PIN}_{UNDERLYING}_{cycle}'}</code> — open one to watch its combined premium with
          ENTRY and STOP lines (the STOP tightens as the engine trails). They are view-only: the engine manages the
          position, so BUY/SHORT arming is locked; manage the strategy from Strategies → Options AT. Rows appear within
          ~5 minutes of a cycle opening and drop out when it closes.</LI>
        <LI><strong>Auto · (underlying)</strong> — auto-computed synthetics for the selected underlying: the four
          directional chips <em>Won't Fall / Won't Rise / Break Up / Break Down</em> across the available expiry buckets
          (D0/D1/D2/W0/M0), built as ATM-anchored two-strike-wide spreads on each expiry's own strike grid. The ⟳ button
          recomputes and REPLACES the set; it also regenerates itself at day rollover (crypto at 00:00 UTC; NSE at the
          next session start, skipping weekends and holidays). Armed/live rows survive the rollover. On Delta,
          auto-generation covers BTC &amp; ETH (the liquid chains).</LI>
      </List>
      <P>
        Names encode what a row is: <code>{'{D0|D1|D2|W0|M0}_{Db|Cr}_{L|S}_{UNDERLYING}'}</code> — e.g.{' '}
        <code>D1_Db_L_BTC</code> is a next-day-expiry <em>debit</em> spread aligned with a <em>long</em> (up) view.
        Colours say the same at a glance: <Sw c="#2e7d32" />darker green = up-aligned debit, <Sw c="#81c784" />lighter
        green = up-aligned credit, <Sw c="#c62828" />darker red = down-aligned debit, <Sw c="#ef9a9a" />lighter red =
        down-aligned credit. Right-click a row to <strong>Delete</strong> it (blocked while live) or{' '}
        <strong>Disarm</strong> its trigger.
      </P>

      <H id="chart">The combined chart &amp; the trigger lock</H>
      <P>
        Clicking a synthetic opens a chart of the <strong>package's own premium</strong> (all legs combined,
        ratio-weighted) — not the underlying. A <em>credit</em> structure charts as the package you <em>sell</em>; a{' '}
        <em>debit</em> structure as the package you <em>buy</em> — so on every synthetic chart, <strong>up always means
        your position profits</strong>. That is also why the trigger direction is locked purely by Debit/Credit,
        regardless of the up/down view:
      </P>
      <List dense>
        <LI><strong>Debit synthetic → BUY (LONG) trigger only.</strong> You buy the package; profit as its value rises.
          The SHORT button is disabled and the server rejects a short as a safety net.</LI>
        <LI><strong>Credit synthetic → SHORT trigger only.</strong> You sell the package; profit as its premium decays.
          The BUY button is disabled and the server rejects a long.</LI>
      </List>

      <H id="triggers">Arming a trigger (the same 3 clicks)</H>
      <List dense>
        <LI>Pick the mode with the <Chip size="small" label="PAPER" sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#546e7a', color: '#fff' }} /> /{' '}
          <Chip size="small" label="LIVE" sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#c62828', color: '#fff' }} /> chip (LIVE asks for an explicit
          confirmation — real multi-leg orders will fire). There is no automatic trailing stop: move the stop yourself
          by dragging its line.</LI>
        <LI>Click BUY or SHORT, then click the chart three times: <strong>entry → stop-loss → target</strong> (all in
          premium units — the chart's own axis). The confirm dialog shows the levels and the lots.</LI>
        <LI>The armed trigger renders as the usual draggable lines — drag to move the levels, click the × to disarm.
          Entries are classified DIP (price comes back to the level) or STOP (price breaks through) automatically.</LI>
        <LI>Every ~30s the app checks the live combined premium. A cross must persist into the next check before firing
          (one bad mark can't fire an entry), and if the premium runs through your STOP side before the entry ever
          fires, the trigger is cancelled instead of entering into a loss.</LI>
      </List>
      <P>
        <strong>On fire</strong>, the multi-leg basket is placed through the same hardened path every basket uses
        (protective BUY legs first, fill-confirmed, kill-switch/daily-loss gates; LIVE additionally passes a wallet
        margin pre-check with 10% headroom). Your drawn SL/Target become the basket's auto-close levels, and a
        mandatory square-off runs <strong>15 minutes before the nearest leg's expiry</strong>. The SL / Target are
        <strong> net</strong> levels: the monitor compares them with the premium a close would really pay (the bid /
        ask) plus the entry and exit fees, so the line labels are what you receive.
      </P>

      <H id="lifecycle">Lifecycle &amp; safety rails</H>
      <List dense>
        <LI>SAVED → ARMED (trigger set) → LIVE (fired; shows in <em>Synthetic · live</em> and in Trades → Option
          baskets) → CLOSED (target/stop/manual/pre-expiry square-off).</LI>
        <LI>A synthetic whose leg has <strong>expired</strong> is removed automatically; arming and firing are blocked
          within <strong>30 minutes of the nearest expiry</strong> (expiry-day gamma).</LI>
        <LI>If a fire attempt fails (margin, chain, network) it retries and <strong>stands down after 3 failures</strong>{' '}
          with a Telegram alert — never a silent loop.</LI>
        <LI><strong>Out-of-sync protection:</strong> every ~5 minutes open LIVE baskets are compared against the
          broker's real positions. If a leg was closed or reduced outside the app, the basket is flagged{' '}
          <em>⛓ Out of sync</em>, automatic TP/SL freezes (no blind exits), and you choose: <em>Adopt broker truth</em>{' '}
          (rewrite the basket to what actually remains and resume) or close whatever is really there.</LI>
      </List>

      <H id="synthiya">The Synthiya indicator</H>
      <P>
        <strong>Synthiya</strong> (Oscillators group) plots three <em>staircase</em> lines in the lower pane for the
        synthetic's <strong>underlying</strong>: the put-call ratio (PCR), the ATM implied volatility, and the
        relative strength vs the benchmark. Each step is coloured with its up-colour when it rises vs the previous
        step and its down-colour when it falls — all six colours and the line widths are editable in Chart Settings →
        Synthiya. It works on regular optionable charts too.
      </P>

      <GuideCallout kind="warning" title="Know the current limits">
        The synthetic chart is a snapshot — it doesn't tick live (reopen the symbol to refresh; the server-side trigger
        monitoring is unaffected). NSE synthetics need an active Zerodha session and evaluate only during market hours.
        The roll/adjust engine for baskets remains Delta-only — for an NSE basket, close and re-open instead.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'Synthetic symbols', body: 'save option basket as symbol chip synthetic pane live auto D0 D1 D2 W0 M0 combined premium chart' },
    { heading: 'Trigger lock', anchor: 'chart', body: 'debit long buy only credit short sell only package premium direction lock' },
    { heading: 'Arming triggers', anchor: 'triggers', body: 'BUY SHORT 3 clicks entry stop target PAPER LIVE TRL trailing margin pre-check fire 30s DIP STOP' },
    { heading: 'Lifecycle & expiry', anchor: 'lifecycle', body: 'expired removed 30 minutes block 15 minute square off out of sync adopt broker truth desynced frozen' },
    { heading: 'Synthiya indicator', anchor: 'synthiya', body: 'staircase PCR IV relative strength lower pane colors chart settings' },
  ],
};

export default Section_synthetic_symbols;
